import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Post } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { FormField, Input } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';

/** Today as yyyy-mm-dd in LOCAL time — `toISOString` would hand back UTC and shift the day. */
function todayLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Record that an export post went live.
 *
 * An export destination has no API to publish through, so this is a person telling us what happened
 * — which is why it asks for a URL rather than producing one.
 *
 * A FUTURE date is allowed only when the client's own platform has the post scheduled, and only with
 * a live URL. That pairing is the whole point: WordPress and its peers assign a permalink when a post
 * is scheduled rather than when it fires, so the URL existing is the evidence the schedule is real.
 * Without it a future date is an assertion about a publication nothing has agreed to.
 */
export function MarkPublishedModal({
  post,
  onClose,
  onDone,
}: {
  /** The post being marked. Null keeps the dialog closed. */
  post: Post | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const urlId = useFieldId('mark-published-url');
  const dateId = useFieldId('mark-published-date');
  const [liveUrl, setLiveUrl] = useState('');
  const [publishDate, setPublishDate] = useState(todayLocal());
  const [scheduledOnPlatform, setScheduledOnPlatform] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isFuture = publishDate > todayLocal();
  /**
   * The one rule worth stating before submit rather than after: ticking the box without a URL.
   * The server refuses it too — this exists so the refusal arrives while the field is still in front
   * of the person who has to fill it.
   */
  const missingUrl = scheduledOnPlatform && !liveUrl.trim();
  const futureWithoutBox = isFuture && !scheduledOnPlatform;

  const mark = useMutation({
    mutationFn: () =>
      api.markPostPublished(post!.id, {
        liveUrl: liveUrl.trim() || undefined,
        // Local midday, so the day the user picked survives a timezone conversion in either
        // direction — midnight would land on the previous day west of UTC.
        publishAt: new Date(`${publishDate}T12:00:00`).toISOString(),
        scheduledOnPlatform,
      }),
    onSuccess: () => {
      reset();
      onDone();
    },
    onError: (err) =>
      setError(
        err instanceof ApiRequestError ? err.message : 'Could not mark this post published.',
      ),
  });

  const reset = () => {
    setLiveUrl('');
    setPublishDate(todayLocal());
    setScheduledOnPlatform(false);
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open={post != null}
      onClose={close}
      title="Mark as published"
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            loading={mark.isPending}
            disabled={missingUrl || futureWithoutBox || !publishDate}
            onClick={() => mark.mutate()}
          >
            Mark published
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormField label="Live URL" htmlFor={urlId}>
          <Input
            id={urlId}
            value={liveUrl}
            placeholder="https://yoursite.com/blog/post-slug"
            onChange={(e) => setLiveUrl(e.target.value)}
          />
        </FormField>

        <FormField label="Publish date" htmlFor={dateId}>
          <Input
            id={dateId}
            type="date"
            value={publishDate}
            // Capped at today unless the platform has it scheduled — the common case is recording a
            // post that is already live, and an accidental future date would silently park it in
            // Scheduled instead of publishing it.
            max={scheduledOnPlatform ? undefined : todayLocal()}
            onChange={(e) => setPublishDate(e.target.value)}
          />
        </FormField>

        <label className="flex cursor-pointer items-start gap-2.5 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-line-input"
            checked={scheduledOnPlatform}
            onChange={(e) => setScheduledOnPlatform(e.target.checked)}
          />
          <span>
            <span className="font-medium text-ink">This post is scheduled on the platform</span>
            <span className="block text-xs text-ink-muted">
              Requires Live URL to already be set above.
            </span>
          </span>
        </label>

        {missingUrl && (
          <p className="text-sm text-red-600 dark:text-error-dark">
            Add the live URL your platform assigned when it scheduled this post — without it there is
            nothing for other posts to link to before it goes out.
          </p>
        )}
        {futureWithoutBox && (
          <p className="text-sm text-red-600 dark:text-error-dark">
            That date is in the future. Tick &ldquo;scheduled on the platform&rdquo; if your platform
            has it queued, or choose today or earlier.
          </p>
        )}

        {/* What will actually happen, before it happens. */}
        {!missingUrl && !futureWithoutBox && (
          <p className="rounded-lg bg-page px-3 py-2 text-sm text-ink-label">
            {isFuture
              ? 'This post becomes Scheduled, and turns to Published on that date on its own — the URL is already captured, so nothing more is needed from you.'
              : 'This post becomes Published straight away.'}
          </p>
        )}
        {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
      </div>
    </Modal>
  );
}
